import type { IndustryId } from './types'
import { INDUSTRY_WORKFLOWS, type WorkflowRuleSeed } from './workflows'

/**
 * What choosing an industry actually configures.
 *
 * Picking a sector previously moved the price and nothing else. A clinic and a marketplace
 * need different labels on their chats, different fields on a ticket, and different canned
 * replies — and asking a new customer to invent all of that on day one is why most tools
 * are abandoned during setup. Each blueprint is applied to the customer's own database at
 * provision time and is editable afterwards, so it is a starting point rather than a cage.
 */

export interface LabelSeed {
  name: string
  /** Held here rather than in the UI so a label looks the same everywhere it appears. */
  color: string
  description: string
}

/** Mirrors the field types an operator would expect from a CRM. */
export type PropertyType =
  | 'text'
  | 'date'
  | 'single_select'
  | 'multi_select'
  | 'number'
  | 'file'

export interface PropertySeed {
  name: string
  type: PropertyType
  /** Groups related fields in the side panel, e.g. "Clinical" or "Order". */
  section: string
  options?: string[]
  /**
   * Tickets only. Blocks closing until the field is filled, which is how a regulated
   * business proves an outcome was recorded rather than trusting an agent to remember.
   */
  requiredBeforeClose?: boolean
  hint?: string
}

export interface QuickReplySeed {
  shortcut: string
  title: string
  body: string
}

export interface KnowledgeAnswerSeed {
  title: string
  body: string
  keywords: string[]
}

export interface IndustryBlueprint {
  labels: LabelSeed[]
  chatProperties: PropertySeed[]
  ticketProperties: PropertySeed[]
  quickReplies: QuickReplySeed[]
  /**
   * A handful of starter FAQ answers, not a real knowledge base — every one is written
   * generically enough to need editing before it says anything true about a specific
   * business. The point is that auto-reply's "answer from knowledge base" step, and the
   * gap-analysis screen that reports what it still cannot answer, both have something to
   * work with on day one instead of doing nothing until a person writes the first entry.
   */
  knowledgeAnswers: KnowledgeAnswerSeed[]
  rules: WorkflowRuleSeed[]
  /**
   * The four ticket stages themselves (open/pending/resolved/closed) never change — SLA
   * escalation, resolution analytics and automation conditions all key off exactly those
   * four values. What differs by sector is the *word* a team uses for a stage, most often
   * "pending": a courier's is naturally "Awaiting parts", a clinic's "Awaiting clinician".
   * Only override the stages where the sector term is genuinely clearer than the default —
   * omitting a stage here just leaves it as plain "Open"/"Pending"/etc, and the customer can
   * always rename any of them afterwards from Settings regardless of what's seeded here.
   */
  statusLabels?: Partial<Record<'open' | 'pending' | 'resolved' | 'closed', string>>
  /** Shown during onboarding so the customer understands why these defaults were chosen. */
  rationale: string
}

const PALETTE = {
  red: '#b3261e',
  amber: '#a16207',
  green: '#166534',
  blue: '#1d4ed8',
  violet: '#6d28d9',
  slate: '#475569',
  teal: '#0f766e',
} as const

const COMMON_QUICK_REPLIES: QuickReplySeed[] = [
  {
    shortcut: '/ack',
    title: 'Acknowledge',
    body: "Thanks for getting in touch — we've got this and will come back to you shortly.",
  },
  {
    shortcut: '/hours',
    title: 'Outside hours',
    body: 'Thanks for your message. Our team is offline right now and will reply when we reopen.',
  },
]

export const INDUSTRY_BLUEPRINTS: Record<IndustryId, IndustryBlueprint> = {
  general: {
    rationale:
      'A neutral starting point: enough structure to triage and route, without sector rules that would not apply.',
    labels: [
      { name: 'Sales', color: PALETTE.green, description: 'Prospects and live opportunities' },
      { name: 'Support', color: PALETTE.blue, description: 'Existing customers needing help' },
      { name: 'Escalation', color: PALETTE.red, description: 'Needs a manager' },
      { name: 'Internal', color: PALETTE.slate, description: 'Team-only chats' },
    ],
    chatProperties: [
      { name: 'Company', type: 'text', section: 'Account' },
      { name: 'Account owner', type: 'text', section: 'Account' },
      {
        name: 'Customer tier',
        type: 'single_select',
        section: 'Account',
        options: ['Free', 'Standard', 'Priority'],
      },
    ],
    ticketProperties: [
      {
        name: 'Resolution',
        type: 'single_select',
        section: 'Outcome',
        options: ['Resolved', 'Workaround', 'No action needed', 'Escalated'],
        requiredBeforeClose: true,
      },
    ],
    quickReplies: COMMON_QUICK_REPLIES,
    knowledgeAnswers: [
      {
        title: 'What are your opening hours?',
        body: 'Edit this with your real hours — e.g. "We\'re online Monday to Friday, 9am to 6pm. Outside those hours, messages are answered the next working day."',
        keywords: ['hours', 'open', 'closed', 'available'],
      },
      {
        title: 'How do I speak to a person?',
        body: 'Just keep typing here — every message reaches the team, and someone will pick it up. There is no separate "agent" command needed.',
        keywords: ['human', 'agent', 'person', 'representative'],
      },
      {
        title: 'How do I get a refund or complain about something?',
        body: 'Edit this with your real policy — e.g. "Tell us what happened and we\'ll take it from there. Most requests are resolved within a couple of working days."',
        keywords: ['refund', 'complaint', 'problem', 'issue'],
      },
    ],
    rules: INDUSTRY_WORKFLOWS.general,
  },

  ecommerce: {
    rationale:
      'Retail conversations are almost always about a specific order, so the order number is captured on the chat and a refund cannot be closed without recording the amount.',
    labels: [
      { name: 'Delivery', color: PALETTE.red, description: 'Late, missing or damaged orders' },
      { name: 'Refunds', color: PALETTE.amber, description: 'Returns, exchanges and refunds' },
      { name: 'Order status', color: PALETTE.blue, description: 'Tracking and dispatch queries' },
      { name: 'Pre-sale', color: PALETTE.green, description: 'Questions before buying' },
      { name: 'Wholesale', color: PALETTE.violet, description: 'Trade and bulk buyers' },
    ],
    chatProperties: [
      { name: 'Order number', type: 'text', section: 'Order', hint: 'The most recent order discussed' },
      { name: 'Courier', type: 'single_select', section: 'Order', options: ['DHL', 'FedEx', 'Local', 'Other'] },
      { name: 'Order value', type: 'number', section: 'Order' },
      { name: 'Repeat customer', type: 'single_select', section: 'Account', options: ['Yes', 'No'] },
    ],
    ticketProperties: [
      {
        name: 'Outcome',
        type: 'single_select',
        section: 'Resolution',
        options: ['Refunded', 'Replaced', 'Redelivered', 'Declined', 'No action'],
        requiredBeforeClose: true,
      },
      // Refund totals are the figure finance reconciles against, so it is captured at
      // source rather than reconstructed from the message history later.
      { name: 'Refund amount', type: 'number', section: 'Resolution' },
      { name: 'Proof of delivery', type: 'file', section: 'Resolution' },
    ],
    statusLabels: { pending: 'In progress' },
    quickReplies: [
      ...COMMON_QUICK_REPLIES,
      {
        shortcut: '/track',
        title: 'Share tracking',
        body: 'Here is your tracking link: {{tracking_url}}. It updates every few hours.',
      },
      {
        shortcut: '/refund',
        title: 'Refund started',
        body: "Your refund is on its way and takes 5–7 working days to reach your account. I'll confirm once it clears.",
      },
      {
        shortcut: '/delay',
        title: 'Apologise for delay',
        body: "I'm sorry your order is late. I've chased the courier and will update you today.",
      },
    ],
    knowledgeAnswers: [
      {
        title: "What's your return policy?",
        body: 'Edit this with your real policy — e.g. "Unused items can be returned within 30 days of delivery for a full refund. Sale items are final unless faulty."',
        keywords: ['return', 'policy', 'refund', 'exchange'],
      },
      {
        title: 'How do I track my order?',
        body: "Share your order number and we'll pull up the latest tracking for you — no need to dig through your email for the courier's link.",
        keywords: ['track', 'tracking', 'order', 'delivery', 'shipping'],
      },
      {
        title: 'Do you ship internationally?',
        body: 'Edit this with your real coverage — e.g. "Yes, we ship to most countries. International orders typically take 7–14 working days and customs charges are the buyer\'s responsibility."',
        keywords: ['international', 'ship', 'overseas', 'abroad', 'customs'],
      },
    ],
    rules: INDUSTRY_WORKFLOWS.ecommerce,
  },

  healthcare: {
    rationale:
      'Clinical messages carry risk that ordinary support does not. Labels separate clinical from administrative traffic, and no clinical ticket can be closed without recording who reviewed it.',
    labels: [
      { name: 'Clinical urgent', color: PALETTE.red, description: 'Possible emergencies — never queue these' },
      { name: 'Prescriptions', color: PALETTE.amber, description: 'Repeats, dosage and medication queries' },
      { name: 'Appointments', color: PALETTE.blue, description: 'Booking, rescheduling and reminders' },
      { name: 'Billing', color: PALETTE.slate, description: 'Invoices, insurance and claims' },
      { name: 'Results', color: PALETTE.teal, description: 'Test results and follow-up' },
    ],
    chatProperties: [
      { name: 'Patient reference', type: 'text', section: 'Patient', hint: 'Internal record number, not a full name' },
      { name: 'Registered clinic', type: 'text', section: 'Patient' },
      { name: 'Consultant', type: 'text', section: 'Care' },
      {
        name: 'Consent on file',
        type: 'single_select',
        section: 'Compliance',
        options: ['Yes', 'No', 'Expired'],
        hint: 'Whether the patient has consented to messaging on WhatsApp',
      },
    ],
    ticketProperties: [
      {
        name: 'Reviewed by',
        type: 'text',
        section: 'Clinical',
        // The audit question after an incident is always who saw it and when.
        requiredBeforeClose: true,
        hint: 'The clinician who reviewed this before it was closed',
      },
      {
        name: 'Outcome',
        type: 'single_select',
        section: 'Clinical',
        options: ['Advice given', 'Appointment booked', 'Referred', 'Escalated to emergency care', 'No action needed'],
        requiredBeforeClose: true,
      },
      { name: 'Follow-up due', type: 'date', section: 'Clinical' },
    ],
    statusLabels: { pending: 'Awaiting clinician' },
    quickReplies: [
      {
        shortcut: '/emergency',
        title: 'Emergency redirect',
        body: 'If this is a medical emergency, please call your local emergency number or go to A&E now. This channel is not monitored continuously.',
      },
      {
        shortcut: '/book',
        title: 'Booking link',
        body: 'You can book, move or cancel an appointment here: {{booking_url}}',
      },
      {
        shortcut: '/repeat',
        title: 'Repeat prescription',
        body: 'Your repeat prescription request has been sent to the clinician. Most are reviewed within one working day.',
      },
      ...COMMON_QUICK_REPLIES,
    ],
    knowledgeAnswers: [
      {
        title: 'How do I book, move or cancel an appointment?',
        body: 'Edit this with your real process — e.g. "Tell us your name and preferred date and we\'ll arrange it, or use the booking link we can send you."',
        keywords: ['appointment', 'book', 'reschedule', 'cancel'],
      },
      {
        title: 'How do I request a repeat prescription?',
        body: 'Edit this with your real process — e.g. "Message us the medication name and your patient reference, and we will pass it to the clinician for review, usually within one working day."',
        keywords: ['prescription', 'repeat', 'medication', 'refill'],
      },
      {
        title: 'What should I do in an emergency?',
        body: "This channel is not monitored continuously and must never be relied on for anything urgent — call your local emergency number or go to A&E straight away.",
        keywords: ['emergency', 'urgent', 'ambulance'],
      },
    ],
    rules: INDUSTRY_WORKFLOWS.healthcare,
  },

  education: {
    rationale:
      'Most traffic arrives in cohort groups where individual questions get lost. Labels separate admissions from pastoral concerns, and safeguarding is kept deliberately distinct.',
    labels: [
      { name: 'Safeguarding', color: PALETTE.red, description: 'Welfare concerns — restrict who can see these' },
      { name: 'Admissions', color: PALETTE.green, description: 'Enquiries, applications and fees' },
      { name: 'Academic', color: PALETTE.blue, description: 'Timetables, homework and results' },
      { name: 'Parents', color: PALETTE.violet, description: 'Parent and guardian channels' },
      { name: 'Alumni', color: PALETTE.slate, description: 'Former students' },
    ],
    chatProperties: [
      { name: 'Student reference', type: 'text', section: 'Student' },
      { name: 'Cohort', type: 'text', section: 'Student', hint: 'Year group or class' },
      { name: 'Relationship', type: 'single_select', section: 'Contact', options: ['Student', 'Parent', 'Guardian', 'Staff'] },
    ],
    ticketProperties: [
      {
        name: 'Outcome',
        type: 'single_select',
        section: 'Resolution',
        options: ['Answered', 'Meeting arranged', 'Referred to pastoral', 'Escalated to safeguarding lead'],
        requiredBeforeClose: true,
      },
      { name: 'Escalated to', type: 'text', section: 'Resolution' },
    ],
    statusLabels: { pending: 'Awaiting reply' },
    quickReplies: [
      ...COMMON_QUICK_REPLIES,
      {
        shortcut: '/prospectus',
        title: 'Send prospectus',
        body: 'Here is our prospectus and fee structure: {{prospectus_url}}. Happy to arrange a visit.',
      },
      {
        shortcut: '/timetable',
        title: 'Timetable',
        body: 'The current timetable is here: {{timetable_url}}. Any changes are posted in this group.',
      },
    ],
    knowledgeAnswers: [
      {
        title: 'How do I apply or request a prospectus?',
        body: "Send us your name and the year group you're interested in and we'll get a prospectus and fee structure over to you.",
        keywords: ['apply', 'admission', 'prospectus', 'enrol', 'enrolment'],
      },
      {
        title: 'Where can I find the timetable?',
        body: 'Edit this with your real link — e.g. "The current timetable is on the parent portal. Any last-minute changes are also posted in this chat."',
        keywords: ['timetable', 'schedule', 'classes', 'lessons'],
      },
      {
        title: 'I have a welfare or safeguarding concern — what do I do?',
        body: 'Tell us as much as you feel able to — this is routed straight to the safeguarding lead and treated as a priority, not queued with general messages.',
        keywords: ['safeguarding', 'welfare', 'concern', 'bullying'],
      },
    ],
    rules: INDUSTRY_WORKFLOWS.education,
  },

  financial: {
    rationale:
      'Regulators measure from first contact, so complaints and suspected fraud are labelled on arrival rather than when an agent recognises one. Closing either requires a recorded outcome.',
    labels: [
      { name: 'Fraud', color: PALETTE.red, description: 'Suspected fraud — act immediately' },
      { name: 'Complaint', color: PALETTE.amber, description: 'Regulated complaints, clock starts on arrival' },
      { name: 'Servicing', color: PALETTE.blue, description: 'Statements, transfers and limits' },
      { name: 'KYC', color: PALETTE.violet, description: 'Identity and document collection' },
      { name: 'Advice', color: PALETTE.teal, description: 'Product questions' },
    ],
    chatProperties: [
      { name: 'Client reference', type: 'text', section: 'Client' },
      { name: 'Relationship manager', type: 'text', section: 'Client' },
      {
        name: 'KYC status',
        type: 'single_select',
        section: 'Compliance',
        options: ['Verified', 'Pending', 'Expired', 'Failed'],
      },
      {
        name: 'Vulnerable customer',
        type: 'single_select',
        section: 'Compliance',
        options: ['Yes', 'No', 'Unknown'],
        hint: 'Drives the duty of care owed on this conversation',
      },
    ],
    ticketProperties: [
      {
        name: 'Complaint reference',
        type: 'text',
        section: 'Regulatory',
        hint: 'The reference reported to the regulator',
      },
      {
        name: 'Outcome',
        type: 'single_select',
        section: 'Regulatory',
        options: ['Upheld', 'Partially upheld', 'Not upheld', 'Resolved informally', 'Referred to ombudsman'],
        requiredBeforeClose: true,
      },
      {
        name: 'Redress paid',
        type: 'number',
        section: 'Regulatory',
      },
      { name: 'Final response sent', type: 'date', section: 'Regulatory', requiredBeforeClose: true },
    ],
    statusLabels: { pending: 'Under review' },
    quickReplies: [
      {
        shortcut: '/fraud',
        title: 'Fraud first response',
        body: "We're treating this as urgent. Please do not approve any further requests. A specialist will contact you within the hour.",
      },
      {
        shortcut: '/complaint',
        title: 'Complaint acknowledged',
        body: 'Thank you for raising this. Your complaint is logged under {{complaint_reference}} and we will send a written response.',
      },
      ...COMMON_QUICK_REPLIES,
    ],
    knowledgeAnswers: [
      {
        title: 'How do I raise a complaint?',
        body: "Tell us what happened and we'll log it straight away — you'll get a reference number and a written response within the timeframe your agreement sets out.",
        keywords: ['complaint', 'complain', 'dispute', 'unhappy'],
      },
      {
        title: "I think I've been a victim of fraud — what do I do?",
        body: "Tell us now and do not approve or authorise anything further in the meantime. This is treated as urgent and a specialist will contact you directly.",
        keywords: ['fraud', 'scam', 'unauthorised', 'stolen'],
      },
      {
        title: 'What documents do I need for identity verification?',
        body: 'Edit this with your real requirements — e.g. "A government-issued photo ID and a recent proof of address, both less than three months old."',
        keywords: ['kyc', 'identity', 'verification', 'documents', 'id'],
      },
    ],
    rules: INDUSTRY_WORKFLOWS.financial,
  },

  field_services: {
    rationale:
      'Work happens away from a desk and gets reported in dispatch groups. Labels follow the job rather than the customer, and a job cannot be closed without a completion record.',
    labels: [
      { name: 'Safety', color: PALETTE.red, description: 'Site incidents — highest priority' },
      { name: 'Dispatch', color: PALETTE.amber, description: 'Crews blocked or delayed on site' },
      { name: 'Scheduling', color: PALETTE.blue, description: 'Bookings, ETAs and reschedules' },
      { name: 'Quotes', color: PALETTE.green, description: 'Estimates and new work' },
      { name: 'Suppliers', color: PALETTE.slate, description: 'Parts and subcontractors' },
    ],
    chatProperties: [
      { name: 'Job number', type: 'text', section: 'Job' },
      { name: 'Site address', type: 'text', section: 'Job' },
      { name: 'Assigned technician', type: 'text', section: 'Job' },
      {
        name: 'Service type',
        type: 'multi_select',
        section: 'Job',
        options: ['Install', 'Repair', 'Inspection', 'Emergency call-out'],
      },
    ],
    ticketProperties: [
      {
        name: 'Job status',
        type: 'single_select',
        section: 'Completion',
        options: ['Completed', 'Parts on order', 'Revisit booked', 'Cancelled'],
        requiredBeforeClose: true,
      },
      { name: 'Completion photo', type: 'file', section: 'Completion' },
      { name: 'Revisit date', type: 'date', section: 'Completion' },
    ],
    statusLabels: { pending: 'Awaiting parts' },
    quickReplies: [
      ...COMMON_QUICK_REPLIES,
      {
        shortcut: '/eta',
        title: 'Technician ETA',
        body: 'Your technician is on the way and should arrive around {{eta}}. They will call on arrival.',
      },
      {
        shortcut: '/parts',
        title: 'Waiting on parts',
        body: "We're waiting on a part for your job. I'll confirm a revisit date as soon as it lands.",
      },
    ],
    knowledgeAnswers: [
      {
        title: 'How do I get a quote?',
        body: 'Tell us what work you need done and the site address, and we\'ll get an estimate back to you — most quotes go out within a working day.',
        keywords: ['quote', 'estimate', 'price', 'cost'],
      },
      {
        title: 'When will the technician arrive?',
        body: "Share your job number and we'll confirm the latest ETA — the technician will also message directly as they get close.",
        keywords: ['eta', 'technician', 'arrival', 'when'],
      },
      {
        title: 'What do I do if there is a safety issue on site?',
        body: 'Say so immediately and stay safe — this is treated as the highest priority and a controller will respond straight away, day or night.',
        keywords: ['safety', 'incident', 'accident', 'emergency', 'site'],
      },
    ],
    rules: INDUSTRY_WORKFLOWS.field_services,
  },

  logistics: {
    rationale:
      'Consignments matter more than customers here, so the tracking reference sits on the chat and every exception has to be closed with a recorded cause. Drivers escalate from the road, so incidents are labelled to be visible at a glance.',
    labels: [
      { name: 'Incident', color: PALETTE.red, description: 'Accidents, breakdowns and theft — a driver may be at risk' },
      { name: 'Exception', color: PALETTE.amber, description: 'Failed, damaged or refused deliveries' },
      { name: 'Scheduling', color: PALETTE.blue, description: 'Reroutes, ETAs and delays' },
      { name: 'Documentation', color: PALETTE.slate, description: 'Proof of delivery and paperwork' },
      { name: 'Depot', color: PALETTE.teal, description: 'Depot and shift coordination groups' },
    ],
    chatProperties: [
      { name: 'Consignment number', type: 'text', section: 'Shipment' },
      { name: 'Vehicle registration', type: 'text', section: 'Fleet' },
      { name: 'Driver name', type: 'text', section: 'Fleet' },
      {
        name: 'Depot',
        type: 'single_select',
        section: 'Fleet',
        options: ['North', 'South', 'East', 'West', 'Central'],
        hint: 'Which depot owns this route',
      },
    ],
    ticketProperties: [
      {
        name: 'Exception cause',
        type: 'single_select',
        section: 'Resolution',
        options: ['Address incorrect', 'Customer unavailable', 'Damaged in transit', 'Refused', 'Vehicle issue', 'Weather'],
        // Cause codes are what carrier performance is argued over, so they are captured
        // while the driver still remembers rather than reconstructed at month end.
        requiredBeforeClose: true,
      },
      {
        name: 'Outcome',
        type: 'single_select',
        section: 'Resolution',
        options: ['Redelivered', 'Returned to depot', 'Collected by customer', 'Written off'],
        requiredBeforeClose: true,
      },
      { name: 'Proof of delivery', type: 'file', section: 'Resolution' },
      { name: 'Redelivery date', type: 'date', section: 'Resolution' },
    ],
    statusLabels: { pending: 'Investigating' },
    quickReplies: [
      {
        shortcut: '/incident',
        title: 'Driver incident',
        body: 'Stay where you are and stay safe. Do not move the vehicle. A controller is calling you now.',
      },
      {
        shortcut: '/eta',
        title: 'Updated ETA',
        body: 'Your delivery is now expected around {{eta}}. The driver will message on approach.',
      },
      {
        shortcut: '/pod',
        title: 'Send proof of delivery',
        body: 'Here is the proof of delivery for consignment {{consignment_number}}: {{pod_url}}',
      },
      ...COMMON_QUICK_REPLIES,
    ],
    knowledgeAnswers: [
      {
        title: 'Where is my delivery?',
        body: "Share your consignment number and we'll pull the latest status — no need to wait on hold for a tracking update.",
        keywords: ['track', 'tracking', 'where', 'delivery', 'consignment'],
      },
      {
        title: 'My delivery was refused or failed — what happens now?',
        body: 'Edit this with your real process — e.g. "We\'ll attempt redelivery the next working day, or you can arrange collection from the depot — just let us know which you\'d prefer."',
        keywords: ['failed', 'refused', 'missed', 'redelivery'],
      },
      {
        title: 'What do I do if there is an accident or breakdown?',
        body: 'Stay where you are, stay safe, and do not move the vehicle — message us now and a controller will call immediately.',
        keywords: ['incident', 'accident', 'breakdown', 'emergency'],
      },
    ],
    rules: INDUSTRY_WORKFLOWS.logistics,
  },
}

/** Counts used to show what an industry choice sets up before it is committed. */
export function blueprintSummary(industryId: IndustryId) {
  const blueprint = INDUSTRY_BLUEPRINTS[industryId]
  return {
    labels: blueprint.labels.length,
    chatProperties: blueprint.chatProperties.length,
    ticketProperties: blueprint.ticketProperties.length,
    quickReplies: blueprint.quickReplies.length,
    knowledgeAnswers: blueprint.knowledgeAnswers.length,
    rules: blueprint.rules.length,
    // The tightest response target the sector demands, which is the number most likely to
    // change how a team staffs the inbox.
    tightestSlaMinutes: Math.min(...blueprint.rules.map((rule) => rule.slaMinutes)),
    mandatoryTicketFields: blueprint.ticketProperties.filter((p) => p.requiredBeforeClose).length,
  }
}
